/* eslint-disable @atlassian/relay/graphql-naming */
import { graphql, readInlineData } from 'react-relay';
import { commitLocalUpdate, type useRelayEnvironment } from 'react-relay/hooks';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import type { optimisticUpdatesSelectedOptionsQuery$key } from '@atlassian/jira-relay/src/__generated__/optimisticUpdatesSelectedOptionsQuery.graphql';
import { JiraIssueFieldOptionAri } from '@atlassian/ari/jira/issue-field-option';
import { KNOWN_COLUMN_TYPES } from '@atlassian/jira-issue-table-common/src/constants.tsx';

export const getSelectedOptions = (
	queryRef: optimisticUpdatesSelectedOptionsQuery$key | null,
	fieldId: string,
	type: string,
) => {
	if (!queryRef) {
		return;
	}

	const selectedOptions = readInlineData<optimisticUpdatesSelectedOptionsQuery$key>(
		graphql`
			fragment optimisticUpdatesSelectedOptionsQuery on JiraIssueConnection
			@inline
			@argumentDefinitions(
				isJscInfiniteScrollEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-jsc-infinite-scroll-enabled.relayprovider"
				}
			) {
				edges {
					fieldSets(first: 500)
						@optIn(to: "JiraSpreadsheetComponent-M1")
						@include(if: $isJscInfiniteScrollEnabled) {
						edges {
							node {
								type
								fields {
									... on JiraIssueFieldConnection {
										edges {
											node {
												__typename
												fieldId
												type
												... on JiraCheckboxesField {
													selectedOptions {
														edges {
															__id
															node {
																id
																value
															}
														}
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		queryRef,
	);
	const selectedOptionMap: { [key: string]: string } = {};

	if (type === KNOWN_COLUMN_TYPES.CHECKBOX_SELECT) {
		selectedOptions?.edges?.forEach((issue) => {
			const fieldsets = issue?.fieldSets?.edges?.filter(
				(fieldSet) => fieldSet?.node?.type === KNOWN_COLUMN_TYPES.CHECKBOX_SELECT,
			);

			fieldsets?.forEach((fieldSet) => {
				const field = fieldSet?.node?.fields?.edges?.find((f) => f?.node?.fieldId === fieldId);

				field?.node?.selectedOptions?.edges?.forEach((option) => {
					if (option?.__id != null && option?.node?.id != null) {
						const optionId: string = option?.__id ?? '';
						selectedOptionMap[optionId] = option?.node?.id;
					}
				});
			});
		});
	}

	return selectedOptionMap;
};

export const optimisticUpdatesSelectedOptions = (
	environment: ReturnType<typeof useRelayEnvironment>,
	type: string,
	selectedOptionMap: {
		[key: string]: string;
	},
	options: { id: string; value: string }[],
) => {
	if (type !== KNOWN_COLUMN_TYPES.CHECKBOX_SELECT) {
		return;
	}

	commitLocalUpdate(environment, (store: RecordSourceSelectorProxy) => {
		const ids = Object.keys(selectedOptionMap);
		if (ids.length > 0) {
			if (type === KNOWN_COLUMN_TYPES.CHECKBOX_SELECT) {
				const optionMap: { [x: string]: string } = {};
				options.forEach((opt) => {
					optionMap[opt.id] = opt.value;
				});

				ids.forEach((id: string) => {
					const option = store.get(id)?.getLinkedRecord('node');

					const optionId = JiraIssueFieldOptionAri.parse(selectedOptionMap[id]).optionId;
					option?.setValue(optionMap[optionId], 'value');
				});
			}
		}
	});
};
